import type React from 'react';
import { useMemo } from 'react';

import type { ColumnDef } from '@tanstack/react-table';
import type { RoleRecord } from '@vlabs/api/artemy/model';
import { useHasPermission, usePageAccess } from '@vlabs/permissions';
import { dateFormatter } from '@vlabs/uikit/components/table-view/cell/formatters/date';

import { DeleteRolesIconButton } from '@vlabs/monokit/features/point/delete-roles';
import { DuplicateRoleIconButton } from '@vlabs/monokit/features/point/duplicate-role';
import { GoToRoleIconButton } from '@vlabs/monokit/features/point/go-to-role';
import { valueOrHyphen } from '@vlabs/monokit/shared/lib/utils';
import { TableView } from '@vlabs/monokit/shared/ui/table-view';
import { TruncatedValue } from '@vlabs/monokit/shared/ui/truncated-value';

import { useRoleListPageTranslation } from '../i18n';
// eslint-disable-next-line import-x/no-namespace
import * as cells from './cells';
import { RoleIdCell } from './cells/role-id-cell';

function RoleListTable({ roles }: { roles: RoleRecord[] }): React.JSX.Element {
  const { t } = useRoleListPageTranslation();
  const canCreateRole = useHasPermission('roles', 'create');
  const canDeleteRole = useHasPermission('roles', 'delete');
  const canViewRoleDetails = usePageAccess('roles_details');

  const columns: ColumnDef<RoleRecord>[] = useMemo(() => ([
    {
      accessorKey: 'name',
      header: cells.renderHeader(cells.nameCell, t('table.name')),
      cell: cells.renderCell(cells.nameCell, (ctx) => <TruncatedValue value={ctx.getValue()} />),
    },
    {
      accessorKey: 'description',
      header: cells.renderHeader(cells.descriptionCell, t('table.description')),
      cell: cells.renderCell(cells.descriptionCell, (ctx) => <TruncatedValue value={ctx.getValue()} />),
    },
    {
      accessorKey: 'role_id',
      header: cells.renderHeader(cells.idCell, t('table.role_id')),
      cell: cells.renderCell(cells.idCell, (ctx) => (
        <RoleIdCell
          copiedTitle={t('actions.copied_role_id')}
          copyTitle={t('actions.copy_role_id')}
          roleId={ctx.getValue()}
        />
      )),
    },
    {
      accessorKey: 'create_time',
      header: cells.renderHeader(cells.createTimeCell, t('table.create_time')),
      cell: cells.renderCell(cells.createTimeCell, (ctx) => valueOrHyphen(dateFormatter(ctx.getValue()))),
    },
    {
      accessorKey: 'update_time',
      header: cells.renderHeader(cells.updateTimeCell, t('table.update_time')),
      cell: cells.renderCell(cells.updateTimeCell, (ctx) => valueOrHyphen(dateFormatter(ctx.getValue()))),
    },
    ...(canViewRoleDetails ? [{
      accessorKey: 'role_id' as const,
      id: 'goToRole',
      header: cells.renderHeader(cells.actionCell),
      cell: cells.renderCell(cells.actionCell, (ctx) => (ctx.getValue() ? (
        <GoToRoleIconButton
          roleId={ctx.getValue()}
          title={t('actions.go_to_role')}
        />
      ) : null)),
    }] : []),
    ...(canCreateRole ? [{
      accessorKey: 'role_id' as const,
      id: 'duplicateRole',
      header: cells.renderHeader(cells.actionCell),
      cell: cells.renderCell(cells.actionCell, (ctx) => (ctx.getValue() ? (
        <DuplicateRoleIconButton
          roleId={ctx.getValue()}
          title={t('actions.duplicate_role')}
        />
      ) : null)),
    }] : []),
    ...(canDeleteRole ? [{
      accessorKey: 'role_id' as const,
      id: 'deleteRole',
      header: cells.renderHeader(cells.actionCell),
      cell: cells.renderCell(cells.actionCell, (ctx) => (ctx.getValue() ? (
        <DeleteRolesIconButton
          disabled={ctx.row.original.is_system}
          roleIds={[ctx.getValue()]}
          title={t('actions.delete_role')}
        />
      ) : null)),
    }] : []),
    { cell: cells.dividerCell(undefined), id: 'divider' },
  ] as ColumnDef<RoleRecord>[]), [t, canCreateRole, canDeleteRole, canViewRoleDetails]);

  return (
    <TableView
      columns={columns}
      data={roles}
    />
  );
}

export { RoleListTable };
